import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { driversAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Target, Info, Library, CheckCircle, GripVertical } from 'lucide-react';
// Recommended drivers library
const DRIVER_LIBRARY = [
  {
    name: 'Sentiment',
    description: 'Overall emotional response and attitude towards the project or initiative',
    icon: '💭'
  },
  {
    name: 'Engagement',
    description: 'Level of involvement, participation, and active interest in project activities',
    icon: '🤝'
  },
  {
    name: 'Influence',
    description: 'Perceived ability to impact decisions and outcomes related to the project',
    icon: '⚡'
  },
  {
    name: 'Interest',
    description: 'Degree of curiosity and attention stakeholders have in project progress',
    icon: '🎯'
  },
  {
    name: 'Improvement',
    description: 'Perception of positive change and progress over time',
    icon: '📈'
  },
  {
    name: 'Confidence',
    description: 'Trust and belief in project success and leadership capability',
    icon: '✨'
  },
  {
    name: 'Culture',
    description: 'Alignment with organizational values and working environment',
    icon: '🏛️'
  },
  {
    name: 'Relationships',
    description: 'Quality of interpersonal connections and collaboration within the project',
    icon: '🔗'
  }
];

export default function DriversPage() {
  const { projectId } = useParams();
  const { currentProject, drivers, refreshDrivers, selectProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedLibraryDrivers, setSelectedLibraryDrivers] = useState([]);
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [localDrivers, setLocalDrivers] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await selectProject(projectId);
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentProject, selectProject]);

  // Keep local drivers in sync with context
  useEffect(() => {
    setLocalDrivers(drivers);
  }, [drivers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingDriver) {
        await driversAPI.update(projectId, editingDriver.id, formData);
        toast.success('Driver updated');
      } else {
        await driversAPI.create(projectId, formData);
        toast.success('Driver created');
      }
      await refreshDrivers();
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      setEditingDriver(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({ name: driver.name, description: driver.description || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Delete this driver? All associated questions will also be deleted.')) return;
    
    try {
      await driversAPI.delete(projectId, driverId);
      toast.success('Driver deleted');
      await refreshDrivers();
    } catch (err) {
      toast.error('Failed to delete driver');
    }
  };

  const openCreateDialog = () => {
    setEditingDriver(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const toggleLibraryDriver = (driverName) => {
    setSelectedLibraryDrivers(prev => 
      prev.includes(driverName)
        ? prev.filter(n => n !== driverName)
        : [...prev, driverName]
    );
  };

  const handleAddFromLibrary = async () => {
    if (selectedLibraryDrivers.length === 0) {
      toast.error('Please select at least one driver');
      return;
    }

    setAddingFromLibrary(true);
    
    try {
      for (const driverName of selectedLibraryDrivers) {
        const libraryDriver = DRIVER_LIBRARY.find(d => d.name === driverName);
        if (libraryDriver) {
          await driversAPI.create(projectId, {
            name: libraryDriver.name,
            description: libraryDriver.description
          });
        }
      }
      
      toast.success(`Added ${selectedLibraryDrivers.length} driver(s)`);
      await refreshDrivers();
      setLibraryOpen(false);
      setSelectedLibraryDrivers([]);
    } catch (err) {
      toast.error('Failed to add drivers from library');
    } finally {
      setAddingFromLibrary(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    // Reorder locally first for immediate feedback
    const newDrivers = [...localDrivers];
    const [draggedItem] = newDrivers.splice(draggedIndex, 1);
    newDrivers.splice(dropIndex, 0, draggedItem);
    setLocalDrivers(newDrivers);

    // Update on server
    try {
      const driverIds = newDrivers.map(d => d.id);
      await driversAPI.reorder(projectId, driverIds);
      await refreshDrivers();
    } catch (err) {
      toast.error('Failed to reorder drivers');
      setLocalDrivers(drivers); // Revert on error
    }
  };

  // Check which library drivers already exist
  const existingDriverNames = localDrivers.map(d => d.name.toLowerCase());
  const isDriverExists = (name) => existingDriverNames.includes(name.toLowerCase());

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Drivers
          </h1>
          <p className="text-slate-600 mt-1">
            Define the categories that drive stakeholder sentiment
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Library Button */}
          <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                onClick={() => setSelectedLibraryDrivers([])}
                data-testid="driver-library-btn"
              >
                <Library className="mr-2 h-4 w-4" />
                Library
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Driver Library
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Select recommended drivers to add to your project
                </p>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
                <div className="space-y-3">
                  {DRIVER_LIBRARY.map((driver) => {
                    const alreadyExists = isDriverExists(driver.name);
                    const isSelected = selectedLibraryDrivers.includes(driver.name);
                    
                    return (
                      <div
                        key={driver.name}
                        className={`relative flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          alreadyExists 
                            ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                            : isSelected
                              ? 'border-[#5AA2AE] bg-[#5AA2AE]/5'
                              : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => !alreadyExists && toggleLibraryDriver(driver.name)}
                        data-testid={`library-driver-${driver.name.toLowerCase()}`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-lg">
                          {driver.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{driver.name}</h4>
                            {alreadyExists && (
                              <span className="inline-flex items-center text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Added
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-0.5">{driver.description}</p>
                        </div>
                        {!alreadyExists && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleLibraryDriver(driver.name)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-slate-500">
                  {selectedLibraryDrivers.length} selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setLibraryOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    onClick={handleAddFromLibrary}
                    disabled={selectedLibraryDrivers.length === 0 || addingFromLibrary}
                    data-testid="add-from-library-btn"
                  >
                    {addingFromLibrary ? 'Adding...' : `Add ${selectedLibraryDrivers.length || ''} Driver${selectedLibraryDrivers.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Driver Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={openCreateDialog}
                data-testid="create-driver-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Driver
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingDriver ? 'Edit Driver' : 'Create New Driver'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Driver Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Communication, Leadership, Resources"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="driver-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="What does this driver measure?"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="driver-description-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    disabled={submitting}
                    data-testid="driver-submit-btn"
                  >
                    {submitting ? 'Saving...' : (editingDriver ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-[#5AA2AE]/5 border-[#5AA2AE]/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#5AA2AE] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <strong>What are Drivers?</strong> Drivers are the key categories that influence stakeholder sentiment. 
            Drag and drop to reorder them. Questions you create will be grouped under these drivers.
          </div>
        </CardContent>
      </Card>

      {/* Drivers List */}
      {loading ? (
        <Card className="dashboard-card">
          <CardContent className="p-0">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="p-4 border-b border-slate-100 last:border-0">
                <Skeleton className="h-16" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : localDrivers.length === 0 ? (
        <Card className="dashboard-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No drivers yet</h3>
            <p className="text-slate-500 mb-4">Create drivers to organize your survey questions</p>
            <div className="flex items-center justify-center gap-3">
              <Button 
                variant="outline"
                onClick={() => setLibraryOpen(true)}
              >
                <Library className="mr-2 h-4 w-4" />
                Browse Library
              </Button>
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={openCreateDialog}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Custom Driver
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="dashboard-card overflow-hidden">
          <CardContent className="p-0">
            {localDrivers.map((driver, index) => (
              <div
                key={driver.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                className={`group flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 bg-white hover:bg-slate-50 transition-colors cursor-grab active:cursor-grabbing ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
                data-testid={`driver-row-${driver.id}`}
              >
                {/* Drag Handle */}
                <div className="flex items-center gap-3 text-slate-400">
                  <GripVertical className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium w-6 text-center">{index + 1}</span>
                </div>

                {/* Driver Icon */}
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>

                {/* Driver Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">{driver.name}</h3>
                  <p className="text-sm text-slate-500 truncate">
                    {driver.description || 'No description'}
                  </p>
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="opacity-0 group-hover:opacity-100 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(driver)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => handleDelete(driver.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

    </div>
  );
}
